/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMsal } from '@azure/msal-react';
import React from 'react';
import { DataService } from '../../services/dataService';
import type { IMonthSingleItem } from '../../models/IMonthSingleItem';
import {
  Spinner,
  makeStyles,
  tokens,
  Tree,
  TreeItem,
  TreeItemLayout,
  Button,
} from '@fluentui/react-components';
import type { IMonthItem } from '../../models/IMonthItem';
import {
  ClockDismissRegular,
  ArrowDownload20Regular,
  DocumentArrowDown20Regular,
  ErrorCircle20Regular,
} from '@fluentui/react-icons';
import type { IProjectItem } from '../../models/IProjectItem';
import {
  Calendar,
  DateRangeType,
  defaultCalendarStrings,
} from '@fluentui/react-calendar-compat';

const useStyles = makeStyles({
  groupHeader: {
    fontWeight: '600',
    padding: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  groupHeaderHoliday: {
    fontWeight: '600',
    padding: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    color: 'red',
  },
  groupHeaderSaturday: {
    fontWeight: '600',
    padding: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    color: '#fccf03',
  },
  headerRow: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns:
      '70px 70px 70px 80px 80px 80px 80px 80px 80px 100px minmax(200px, 1fr) 80px 150px 150px 150px 150px',
    gap: '8px',
    fontSize: '10px',
    padding: '4px 8px',
    fontWeight: 'bolder',
  },
  dataRow: {
    display: 'grid',
    gridTemplateColumns:
      '70px 70px 70px 80px 80px 80px 80px 80px 80px 100px minmax(200px, 1fr) 80px 150px 150px 150px 150px',
    gap: '8px',
    fontSize: '12px',
    padding: '4px 8px',
  },
  dataRowEven: {
    display: 'grid',
    gridTemplateColumns:
      '70px 70px 70px 80px 80px 80px 80px 80px 80px 100px minmax(200px, 1fr) 80px 150px 150px 150px 150px',
    gap: '8px',
    fontSize: '12px',
    padding: '4px 8px',
    backgroundColor: '#e4e4e4',
  },
  cellValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellValueBold: {
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellValueRed: {
    color: 'red',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellValueSaturday: {
    color: '#fccf03',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellValueWrapped: {
    overflow: 'visible',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: '1.4',
  },
  stackContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  stackRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
  },
  column1: {
    width: '180px',
    fontWeight: 'bolder',
  },
  column2: {
    width: '110px',
  },
  column3: {
    width: '218px',
    fontWeight: 'bolder',
  },
  flexGrow: {
    flexGrow: 3,
    textAlign: 'end',
    paddingRight: '5px',
  },

  projectFlexGrow: {
    flexGrow: 2,
    textAlign: 'end',
    paddingRight: '25px',
  },

  uploadcontainer: {
    '>input': {
      visibility: 'hidden',
      width: 0,
      height: 0,
    },
  },
  icondictionary: {
    fontSize: '25px',
    color: 'rgb(2, 77, 102)',
    cursor: 'pointer',
    margin: '2px',
  },
  iconRed: {
    fontSize: '25px',
    color: 'red',
    margin: '2px',
  },
  treeContainer: {
    width: '100%',
  },
  tree: {
    width: '100%',
  },
  treeItem: {
    width: '100%',
  },
});

interface IGenerateMonthProps {
  mitarbeiterGuid: string;
  selectedDate: Date;
  selectedMonth: IMonthItem | null;
  onClose?: () => void;
}
const GenerateMonth: React.FC<IGenerateMonthProps> = ({
  mitarbeiterGuid,
  selectedDate,
  selectedMonth = null,
  onClose,
}) => {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [dayItems, setDayItems] = React.useState([] as IMonthSingleItem[]);
  const [overview, setOverview] = React.useState({} as IMonthItem);
  const [projectOverview, setProjectOverview] = React.useState(
    [] as IProjectItem[]
  );
  const [mitarbeiterName, setMitarbeiterName] = React.useState('');
  const [groupedDays, setGroupedDays] = React.useState<
    Map<
      string,
      { items: IMonthSingleItem[]; isHoliday: boolean; isSaturday: boolean }
    >
  >(new Map());
  const [gesamtSollStunden, setGesamtSollStunden] = React.useState('00:00');
  const [gesamtSollStundenNumber, setGesamtSollStundenNumber] =
    React.useState(0);
  const [gesamtStunden, setGesamtStunden] = React.useState('00:00');
  const [istStunden, setIstStunden] = React.useState('00:00');
  const [ueberStundenMonat, setUeberStundenMonat] = React.useState('00:00');
  const [numberUeberStundenMonat, setNumberUeberStundenMonat] =
    React.useState(0);
  const [numberUeberStunden, setNumberUeberStunden] = React.useState(0);
  const [ueberStunden, setUeberStunden] = React.useState('00:00');
  const [numberUeberStundenAbzug, setNumberUeberStundenAbzug] =
    React.useState(0);
  const [ueberStundenAbzug, setUeberStundenAbzug] = React.useState('00:00');
  const [gleitzeitStunden, setGleitzeitStunden] = React.useState('00:00');
  const [numberGleitzeitStundenGesamt, setNumberGleitzeitStundenGesamt] =
    React.useState(0);
  const [gleitzeitStundenGesamt, setGleitzeitStundenGesamt] =
    React.useState('00:00');
  const [numberGleitzeitKonto, setNumberGleitzeitKonto] = React.useState(0);
  const [gleitzeitKonto, setGleitzeitKonto] = React.useState('00:00');
  const [numberLabStunden, setNumberLabStunden] = React.useState(0);
  const [labStunden, setLabStunden] = React.useState('00:00');
  const [isLoading, setIsLoading] = React.useState(true);
  const [selectedNewDate, setSelectedNewDate] =
    React.useState<Date>(selectedDate);
  React.useEffect(() => {
    const getStrStunden = (stunden: number, length: number) => {
      const st = stunden;
      const dateMinuten = new Date(0);
      const Komma = st - Math.trunc(st);

      if (Komma > 0) {
        dateMinuten.setMinutes(60 * Komma);
      } else {
        dateMinuten.setMinutes(-(60 * Komma));
      }

      const result =
        st < 0 && Math.trunc(st) === 0
          ? `-${pad(Math.trunc(st), length)}:${pad(dateMinuten.getMinutes(), 2)}`
          : `${pad(Math.trunc(st), length)}:${pad(dateMinuten.getMinutes(), 2)}`;
      return result;
    };

    const pad = (zahl: number, length: number) => {
      let str = '' + zahl;
      let neg = false;
      if (zahl < 0) {
        neg = true;
        str = str.replace('-', '');
      }
      while (str.length < length) {
        str = '0' + str;
      }
      return neg ? '-' + str : str;
    };

    if (!dataService) return;
    if (selectedNewDate === undefined) return;

    let nbUeberStundenAktuell = 0;
    let nbUeberStundenMonat = 0;
    let nbGleitzeitStunden = 0;
    let nbGleitzeitKonto = 0;
    const fetchTage = async (): Promise<void> => {
      setIsLoading(true);
      setUeberStundenMonat(getStrStunden(0, 2));
      setGleitzeitStunden(getStrStunden(0, 2));
      setGleitzeitKonto(getStrStunden(0, 2));
      setNumberLabStunden(0);
      setLabStunden(getStrStunden(0, 2));

      await dataService
        .getMonthCompletion(
          selectedNewDate?.getFullYear().toString(),
          (selectedNewDate?.getMonth() + 1).toString(),
          mitarbeiterGuid
        )
        .then((items: IMonthSingleItem[]): void => {
          setDayItems(items);
          const footerItems = items.filter((f) => f.istFooter);
          const firstItem = items[0];
          const firstFooterItem = footerItems[0];
          let gesamtSoll = 0;
          let tag = '';
          items.forEach((item) => {
            if (tag !== item.strTag) {
              tag = item.strTag;
              if (item.istFooter === false) {
                gesamtSoll += item.sollStunden;
              }
            }
          });
          setGesamtSollStunden(getStrStunden(gesamtSoll, 3));
          const gesamtStunden = footerItems
            .map((s) => s.gesamtStunden)
            .reduce((a, b) => a + b, 0);
          setGesamtSollStundenNumber(gesamtSoll);

          setGesamtStunden(getStrStunden(gesamtStunden, 3));
          const stunden = footerItems
            .map((s) => s.istStunden)
            .reduce((a, b) => a + b, 0);
          setIstStunden(getStrStunden(stunden, 3));

          let ueberstundenAktuell =
            items.length > 0
              ? items[items.length - 1].ueberstundenTagesaktuell
              : 0;
          let ueberstunden = stunden - gesamtSoll;
          // ueberstundenAktuell = ueberstunden;
          let gleitzeitstunden = footerItems
            .map((s) => s.gleitzeitStunden)
            .reduce((a, b) => a + b, 0);

          if (
            firstItem !== undefined &&
            gleitzeitstunden > firstItem.mehrstundenAllIn
          ) {
            ueberstunden += gleitzeitstunden - firstItem.mehrstundenAllIn;
            ueberstundenAktuell +=
              gleitzeitstunden - firstItem.mehrstundenAllIn;
            gleitzeitstunden = firstItem.mehrstundenAllIn;
          }
          if (
            firstItem !== undefined &&
            firstItem.gleitzeit &&
            firstItem.mehrstundenAllIn > 0
          ) {
            if (ueberstunden < 0) ueberstunden = 0;
            nbUeberStundenAktuell = ueberstundenAktuell;
          } else {
            nbUeberStundenAktuell = 0;
            nbUeberStundenMonat = ueberstunden;
            setUeberStundenMonat(getStrStunden(ueberstunden, 2));
          }
          nbGleitzeitStunden = gleitzeitstunden;
          setGleitzeitStunden(
            getStrStunden(gleitzeitstunden, gleitzeitstunden > 99 ? 3 : 2)
          );

          const gleitzeitKonto = firstFooterItem?.mehrstundenAllInJahr ?? 0;
          nbGleitzeitKonto = gleitzeitKonto;
          setGleitzeitKonto(
            getStrStunden(gleitzeitKonto, gleitzeitKonto > 99 ? 3 : 2)
          );

          const labStunden = items
            .filter((f) => f.istFooter === false)
            .map((s) => s.labStunden)
            .reduce((a, b) => a + b, 0);
          setNumberLabStunden(labStunden);
          setLabStunden(getStrStunden(labStunden, labStunden > 99 ? 3 : 2));

          // Group items by day
          const grouped = new Map<
            string,
            {
              items: IMonthSingleItem[];
              isHoliday: boolean;
              isSaturday: boolean;
            }
          >();
          items.forEach((item) => {
            const dayKey = item.strTag.toString();
            if (!grouped.has(dayKey)) {
              grouped.set(dayKey, {
                items: [],
                isHoliday: item.istFeiertag,
                isSaturday: item.istSamstag,
              });
            }
            grouped.get(dayKey)!.items.push(item);
          });
          setGroupedDays(grouped);
        })
        .catch((error) => {
          console.error(error);
        });
      setIsLoading(false);
    };

    const fetchOverview = async (): Promise<void> => {
      await dataService
        .getMonthOverview(selectedNewDate, mitarbeiterGuid)
        .then((item: IMonthItem): void => {
          setMitarbeiterName(item.fullName);
          setOverview(item);
          setErrorMessage('');
          setNumberGleitzeitKonto(nbGleitzeitKonto);
          if (item.vormonatNotExists) {
            setErrorMessage(
              'The previous month has not been completed yet. Please complete the previous month first.'
            );
            setUeberStundenAbzug(getStrStunden(0, 2));
            setNumberUeberStunden(0);
            setUeberStunden(getStrStunden(0, 2));
            setNumberGleitzeitStundenGesamt(nbGleitzeitStunden);
            setGleitzeitStundenGesamt(
              getStrStunden(nbGleitzeitStunden, nbGleitzeitStunden > 99 ? 3 : 2)
            );
          } else {
            if (item.isFuehrungskraft === false) {
              let ueberstunden = 0;
              if (nbGleitzeitKonto > 0) {
                ueberstunden = nbUeberStundenAktuell;
                nbUeberStundenMonat =
                  nbUeberStundenAktuell - item.ueberStundenVormonat;
                setUeberStundenMonat(
                  getStrStunden(
                    nbUeberStundenAktuell - item.ueberStundenVormonat,
                    2
                  )
                );
                // ueberstunden =
                //   nbUeberStundenAktuell + item.ueberStundenVormonat;
                // nbUeberStundenMonat = nbUeberStundenAktuell;
                // setUeberStundenMonat(getStrStunden(nbUeberStundenAktuell, 2));
              } else {
                ueberstunden = item.ueberStundenVormonat + nbUeberStundenMonat;
              }

              let ueberstundenabzug = item.ueberStundenAbzug;
              if (item.ueberStundenAbzug > 0) {
                if (item.ueberStundenAbzug > ueberstunden) {
                  ueberstundenabzug = ueberstunden;
                  ueberstunden = 0;
                } else {
                  ueberstunden = ueberstunden - item.ueberStundenAbzug;
                }
              }
              if (
                item.monat === 8 &&
                item.jahr > 2026 &&
                item.gleitzeit &&
                ueberstunden > item.wochenStunden
              ) {
                const diff = ueberstunden - item.wochenStunden;
                ueberstunden = ueberstunden - diff;
                ueberstundenabzug += diff;
              }
              setNumberUeberStundenAbzug(ueberstundenabzug);

              setGleitzeitStundenGesamt(
                getStrStunden(
                  nbGleitzeitStunden + item.gleitzeitKontoVormonat,
                  nbGleitzeitStunden + item.gleitzeitKontoVormonat > 99 ? 3 : 2
                )
              );
              setNumberGleitzeitStundenGesamt(
                item.gleitzeitKontoVormonat + nbGleitzeitStunden
              );
              setUeberStundenAbzug(
                getStrStunden(ueberstundenabzug, ueberstundenabzug > 99 ? 3 : 2)
              );
              setNumberUeberStunden(ueberstunden);
              setUeberStunden(
                getStrStunden(ueberstunden, ueberstunden > 99 ? 3 : 2)
              );
            }
            setNumberUeberStundenMonat(nbUeberStundenMonat);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    };

    const fetchProjectOverview = async (): Promise<void> => {
      if (selectedNewDate === undefined) return;
      await dataService
        .getProjectsHoursMonth(selectedNewDate, mitarbeiterGuid)
        .then((item: IProjectItem[]): void => {
          setProjectOverview(item);
        })
        .catch((error) => {
          console.error(error);
        });
    };

    const loadData = async () => {
      await fetchTage();
      await fetchOverview();
      await fetchProjectOverview();
    };

    loadData();
  }, [dataService, selectedNewDate, mitarbeiterGuid]);

  const renderCellContent = (item: IMonthSingleItem, columnKey: string) => {
    const fieldContent = item[columnKey as keyof IMonthSingleItem] as string;

    switch (columnKey) {
      case 'strTag':
        return item.istFooter ? (
          <span></span>
        ) : item.istFeiertag ? (
          <span className={styles.cellValueRed}>{fieldContent}</span>
        ) : item.istSamstag ? (
          <span className={styles.cellValueSaturday}>{fieldContent}</span>
        ) : (
          <div className={styles.cellValue}>
            {item.istFuehrungskraft === false &&
              item.sollStunden > 0 &&
              item.gesamtStunden <= 0 &&
              item.typ !== 'Pause' &&
              item.typ !== 'Time back' && (
                <ClockDismissRegular color="red" />
              )}{' '}
            <span>{fieldContent}</span>
          </div>
        );
      case 'strGesamtStunden':
        return item.istFooter ? (
          <span className={styles.cellValueBold}>{fieldContent}</span>
        ) : (
          <span>{fieldContent}</span>
        );
      case 'strIstStunden':
        return item.istFooter ? (
          <span className={styles.cellValueBold}>{fieldContent}</span>
        ) : (
          <span></span>
        );
      case 'strSollStunden':
        return item.istFooter ? (
          <span className={styles.cellValueBold}>{fieldContent}</span>
        ) : (
          <span></span>
        );
      case 'strUStunden1':
        return item.istFooter ? (
          <span className={styles.cellValueBold}>{fieldContent}</span>
        ) : (
          <span></span>
        );
      case 'strUStunden2':
        return item.istFooter ? (
          <span className={styles.cellValueBold}>{fieldContent}</span>
        ) : (
          <span></span>
        );
      case 'strGleitzeitstunden':
        return item.istFooter ? (
          <span className={styles.cellValueBold}>{fieldContent}</span>
        ) : (
          <span></span>
        );
      case 'beschreibung':
        return item.istFooter ? <span></span> : <span>{fieldContent}</span>;
      case 'dienstreise':
        return item.istFooter ? <span></span> : <span>{fieldContent}</span>;
      case 'arbeitspaket':
        return item.istFooter ? <span></span> : <span>{fieldContent}</span>;
      case 'fileUpload':
        return item.istFooter === false && item.taskGuid ? (
          <div className={styles.uploadcontainer}>
            {item.docId && item.docId !== '0' && (
              <span
                className={styles.icondictionary}
                onClick={downloadFile(
                  item.mitarbeiterGuid,
                  item.docId,
                  item.docName
                )}
                style={{ cursor: 'pointer', display: 'inline-flex' }}
              >
                <DocumentArrowDown20Regular />
              </span>
            )}
            {(item.docId === '' || item.docId === '0') &&
              item.docNeedNotExists && (
                <ErrorCircle20Regular className={styles.iconRed} />
              )}
          </div>
        ) : (
          <span></span>
        );
      default:
        return <span className={styles.cellValue}>{fieldContent}</span>;
    }
  };

  const downloadFile = (
    mitarbeiterGuid: string,
    dokid: string,
    dokname: string
  ): React.MouseEventHandler<HTMLSpanElement> => {
    return async () => {
      if (!dataService) return;
      if (selectedNewDate === undefined) return;
      const str = await dataService.downloadDocument(mitarbeiterGuid, dokid);
      const url = window.URL.createObjectURL(
        new Blob([str], {
          type: str.type,
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = dokname;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      setIsLoading(false);
    };
  };

  const downloadMonthPdf = (): React.MouseEventHandler<HTMLButtonElement> => {
    return async () => {
      if (!dataService) return;
      if (selectedNewDate === undefined) return;
      setIsLoading(true);
      const str = await dataService.getMonthCompletionPdf(
        selectedNewDate?.getFullYear().toString(),
        (selectedNewDate?.getMonth() + 1).toString(),
        mitarbeiterGuid
      );

      const url = window.URL.createObjectURL(
        new Blob([str], {
          type: str.type,
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${mitarbeiterName + '_' + selectedNewDate?.getFullYear().toString() + '_' + (selectedNewDate?.getMonth() + 1).toString()}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      setIsLoading(false);
    };
  };

  const onSelectDate = React.useCallback((date: Date): void => {
    setSelectedNewDate(new Date(date.getFullYear(), date.getMonth(), 1));
  }, []);

  const sendMonth = async () => {
    if (!dataService) return;
    const gesamtStunden = dayItems
      .filter((f) => f.istFooter)
      .map((s) => s.gesamtStunden)
      .reduce((a, b) => a + b);
    const istStunden = dayItems
      .filter((f) => f.istFooter)
      .map((s) => s.istStunden)
      .reduce((a, b) => a + b);
    await dataService
      .addMonthPersonal({
        mitarbeiterGuid: mitarbeiterGuid,
        jahr: selectedNewDate?.getFullYear(),
        monat: selectedNewDate ? selectedNewDate.getMonth() + 1 : 0,
        sollStunden: gesamtSollStundenNumber,
        gesamtStunden: gesamtStunden,
        istStunden: istStunden,
        monatsUeberschuss: numberUeberStundenMonat,
        ueberStunden: numberUeberStunden,
        ueberStundenAbzug: numberUeberStundenAbzug,
        gleitzeitKonto: numberGleitzeitStundenGesamt,
        urlaubsRest: overview.urlaubsRest,
        verbrauchterUrlaub: overview.verbrauchterUrlaub,
        workflowStatusId: 1,
        created: new Date(),
      } as IMonthItem)
      .then((_result) => {
        if (onClose) {
          onClose();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const _sendClicked = () => {
    void sendMonth();
  };

  return (
    <>
      {errorMessage && (
        <div
          style={{
            fontSize: '16px',
            marginBottom: '30px',
            maxWidth: '500px',
            color: 'red',
          }}
        >
          {errorMessage}
        </div>
      )}
      <div className={styles.stackContainer}>
        <div className={styles.stackRow}>
          {selectedMonth === null && (
            <Calendar
              dateRangeType={DateRangeType.Month}
              showGoToToday={false}
              highlightSelectedMonth
              isDayPickerVisible={false}
              onSelectDate={onSelectDate}
              value={selectedNewDate}
              strings={defaultCalendarStrings}
            />
          )}
        </div>
      </div>
      {isLoading && <Spinner size="large" style={{ marginBottom: '10px' }} />}
      {selectedNewDate && isLoading === false && (
        <>
          <hr
            style={{
              height: '2px',
              borderWidth: 0,
              color: 'gray',
              backgroundColor: 'gray',
            }}
          />
          {projectOverview.length > 0 &&
            projectOverview.map((item: IProjectItem) => (
              <>
                <div className={styles.stackContainer}>
                  <div className={styles.stackRow}>
                    <div key={item.itemId} className={styles.column1}>
                      {'KTR '} {item.title}:
                    </div>
                    <div className={styles.column2}>
                      <span>{item.strIstStunden}</span>
                    </div>
                  </div>
                </div>
                <hr
                  style={{
                    height: '2px',
                    borderWidth: 0,
                    color: 'gray',
                    backgroundColor: 'gray',
                  }}
                />
              </>
            ))}
          <div className={styles.stackContainer}>
            <div className={styles.stackRow}>
              {overview.isFuehrungskraft === false && (
                <>
                  <div className={styles.column1}>
                    <span>Previous month overtime:</span>
                  </div>
                  <div className={styles.column2}>
                    <span>{overview.strUeberStundenVormonat}</span>
                  </div>
                </>
              )}
              <div className={styles.column1}>
                <span>Previous month holiday:</span>
              </div>
              <div className={styles.column2}>
                <span>
                  {overview.urlaubVormonat} {'Day(s)'}
                </span>
              </div>
              <div className={styles.column1}>
                <span>Consumed teleworking:</span>
              </div>
              <div className={styles.column2}>
                <span>
                  {overview.homeofficeGanzTageVerbrauch} {'Full day(s)'}
                </span>
              </div>
              <div className={styles.flexGrow}>
                {selectedNewDate && (
                  <div style={{ textAlign: 'end' }}>
                    <Button
                      appearance="subtle"
                      icon={<ArrowDownload20Regular />}
                      onClick={downloadMonthPdf()}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className={styles.stackRow}>
              {overview.isFuehrungskraft === false && (
                <>
                  <div className={styles.column1}>
                    <span>Current month overtime:</span>
                  </div>
                  <div className={styles.column2}>
                    <span>{ueberStundenMonat}</span>
                  </div>
                </>
              )}
              <div className={styles.column1}>
                <span>Consumed holiday:</span>
              </div>
              <div className={styles.column2}>
                <span>
                  {overview.verbrauchterUrlaub} {'Day(s)'}
                </span>
              </div>
              <div className={styles.column1}>
                <span></span>
              </div>
              <div className={styles.column2}>
                <span>
                  {overview.homeofficeHalbTageVerbrauch} {'Half day(s)'}
                </span>
              </div>
            </div>
            <div className={styles.stackRow}>
              {overview.isFuehrungskraft === false && (
                <>
                  <div className={styles.column1}>
                    <span>Accrued overtime:</span>
                  </div>
                  <div className={styles.column2}>
                    <span>{ueberStunden}</span>
                  </div>
                </>
              )}
              <div className={styles.column1}>
                <span>Current holiday:</span>
              </div>
              <div className={styles.column2}>
                <span>
                  {overview.urlaubsRest} {'Day(s)'}
                </span>
              </div>
              <div className={styles.column1}>
                <span></span>
              </div>
              <div className={styles.column2}>
                <span></span>
              </div>
            </div>
            <div className={styles.stackRow}>
              {numberGleitzeitKonto > 0 && (
                <>
                  <div className={styles.column1}>
                    <span>Flexitime Account:</span>
                  </div>
                  <div className={styles.column3}>
                    <span>
                      {gleitzeitStundenGesamt} / {gleitzeitKonto} (
                      {gleitzeitStunden})
                    </span>
                  </div>
                  <div className={styles.column2}>
                    <span></span>
                  </div>
                  <div className={styles.column1}>
                    <span></span>
                  </div>
                </>
              )}
            </div>
            <div className={styles.stackRow}>
              {overview.isFuehrungskraft === false && (
                <>
                  <div className={styles.column1}>
                    <span>
                      {ueberStundenAbzug !== '00:00'
                        ? 'Overtime deduction:'
                        : ''}
                    </span>
                  </div>
                  <div className={styles.column2}>
                    <span>
                      {ueberStundenAbzug !== '00:00' ? ueberStundenAbzug : ''}
                    </span>
                  </div>
                </>
              )}
              <div className={styles.column1}>
                <span></span>
              </div>
              <div className={styles.column2}>
                <span></span>
              </div>
              <div className={styles.column1}>
                <span></span>
              </div>
              <div className={styles.column2}>
                <span></span>
              </div>
              <div className={styles.flexGrow}>
                {overview.workflowStatusId > 2 &&
                  overview.vormonatNotExists === false && (
                    <Button
                      disabled={
                        dayItems.filter((i) => i.beschreibungNeedNotExists)
                          .length > 0
                      }
                      style={{ width: '250px' }}
                      onClick={_sendClicked}
                    >
                      Send
                    </Button>
                  )}
              </div>
            </div>
          </div>
          <hr
            style={{
              height: '2px',
              borderWidth: 0,
              color: 'gray',
              backgroundColor: 'gray',
            }}
          />
          <div className={styles.treeContainer}>
            <Tree
              aria-label="Month completion"
              defaultOpenItems={Array.from(groupedDays.keys())}
              className={styles.tree}
            >
              <TreeItem
                key="header"
                itemType="leaf"
                value="header"
                className={styles.treeItem}
              >
                <TreeItemLayout>
                  <div className={styles.headerRow}>
                    <div style={{ minWidth: 70, maxWidth: 70 }}>Day</div>
                    <div style={{ minWidth: 70, maxWidth: 70 }}>Begin</div>
                    <div style={{ minWidth: 70, maxWidth: 70 }}>End</div>
                    <div style={{ minWidth: 80, maxWidth: 80 }}>
                      Required hours
                    </div>
                    <div style={{ minWidth: 80, maxWidth: 80 }}>Hours</div>
                    <div style={{ minWidth: 80, maxWidth: 80 }}>Ü 50%</div>
                    <div style={{ minWidth: 80, maxWidth: 80 }}>Ü 100%</div>
                    <div style={{ minWidth: 80, maxWidth: 80 }}>
                      Total hours
                    </div>
                    {numberGleitzeitKonto > 0 && (
                      <div style={{ minWidth: 80, maxWidth: 80 }}>
                        Flexitime
                      </div>
                    )}
                    {numberLabStunden > 0 && (
                      <div style={{ minWidth: 80, maxWidth: 80 }}>Lab work</div>
                    )}
                    <div style={{ minWidth: 100, maxWidth: 100 }}>Type</div>
                    <div style={{ minWidth: 200, maxWidth: 200 }}>
                      Description
                    </div>
                    <div style={{ minWidth: 80, maxWidth: 80 }}>
                      Confirmation
                    </div>
                    <div style={{ minWidth: 150, maxWidth: 150 }}>
                      Cost center
                    </div>
                    <div style={{ minWidth: 150, maxWidth: 150 }}>
                      Work package
                    </div>
                    <div style={{ minWidth: 150, maxWidth: 150 }}>
                      Business trip
                    </div>
                    <div style={{ minWidth: 150, maxWidth: 150 }}>
                      Travel time
                    </div>
                  </div>
                </TreeItemLayout>
              </TreeItem>
              {Array.from(groupedDays.entries()).map(([day, group]) => {
                const dayNum = parseInt(day.substring(0, 2) || '0');
                const isEvenDay = dayNum % 2 === 0;

                return (
                  <TreeItem
                    key={day}
                    itemType="branch"
                    value={day}
                    className={styles.treeItem}
                  >
                    <TreeItemLayout>
                      <span
                        className={
                          group.isHoliday
                            ? styles.groupHeaderHoliday
                            : group.isSaturday
                              ? styles.groupHeaderSaturday
                              : styles.groupHeader
                        }
                      >
                        {day}
                      </span>
                    </TreeItemLayout>
                    <Tree>
                      {group.items.map((item, idx) => (
                        <TreeItem
                          key={`${day}-${idx}`}
                          itemType="leaf"
                          className={styles.treeItem}
                        >
                          <TreeItemLayout style={{ paddingLeft: '25px' }}>
                            <div
                              className={
                                isEvenDay ? styles.dataRowEven : styles.dataRow
                              }
                            >
                              <div style={{ minWidth: 70, maxWidth: 70 }}>
                                {renderCellContent(item, 'strTag')}
                              </div>
                              <div style={{ minWidth: 70, maxWidth: 70 }}>
                                {renderCellContent(item, 'von')}
                              </div>
                              <div style={{ minWidth: 70, maxWidth: 70 }}>
                                {renderCellContent(item, 'bis')}
                              </div>
                              <div style={{ minWidth: 80, maxWidth: 80 }}>
                                {renderCellContent(item, 'strSollStunden')}
                              </div>
                              <div style={{ minWidth: 80, maxWidth: 80 }}>
                                {renderCellContent(item, 'strGesamtStunden')}
                              </div>
                              <div style={{ minWidth: 80, maxWidth: 80 }}>
                                {renderCellContent(item, 'strUStunden1')}
                              </div>
                              <div style={{ minWidth: 80, maxWidth: 80 }}>
                                {renderCellContent(item, 'strUStunden2')}
                              </div>
                              <div style={{ minWidth: 80, maxWidth: 80 }}>
                                {renderCellContent(item, 'strIstStunden')}
                              </div>
                              {numberGleitzeitKonto > 0 && (
                                <div style={{ minWidth: 80, maxWidth: 80 }}>
                                  {renderCellContent(
                                    item,
                                    'strGleitzeitstunden'
                                  )}
                                </div>
                              )}
                              {numberLabStunden > 0 && (
                                <div style={{ minWidth: 80, maxWidth: 80 }}>
                                  {renderCellContent(item, 'strLabstunden')}
                                </div>
                              )}
                              <div style={{ minWidth: 100, maxWidth: 100 }}>
                                {renderCellContent(item, 'typ')}
                              </div>
                              <div
                                className={styles.cellValueWrapped}
                                style={{ minWidth: 200, maxWidth: 200 }}
                              >
                                {renderCellContent(item, 'beschreibung')}
                              </div>
                              <div style={{ minWidth: 80, maxWidth: 80 }}>
                                {renderCellContent(item, 'fileUpload')}
                              </div>
                              <div style={{ minWidth: 150, maxWidth: 150 }}>
                                {renderCellContent(item, 'kostentraeger')}
                              </div>
                              <div
                                className={styles.cellValueWrapped}
                                style={{ minWidth: 150, maxWidth: 150 }}
                              >
                                {renderCellContent(item, 'arbeitspaket')}
                              </div>
                              <div
                                className={styles.cellValueWrapped}
                                style={{ minWidth: 150, maxWidth: 150 }}
                              >
                                {renderCellContent(item, 'dienstreise')}
                              </div>
                              <div style={{ minWidth: 150, maxWidth: 150 }}>
                                {renderCellContent(
                                  item,
                                  'reisezeitAktivPassiv'
                                )}
                              </div>
                            </div>
                          </TreeItemLayout>
                        </TreeItem>
                      ))}
                    </Tree>
                  </TreeItem>
                );
              })}
            </Tree>
          </div>
          <hr
            style={{
              height: '2px',
              borderWidth: 0,
              color: 'gray',
              backgroundColor: 'gray',
            }}
          />
          <div
            className={styles.stackContainer}
            style={{ marginBottom: '20px' }}
          >
            <div className={styles.stackRow}>
              <div className={styles.column1}>
                <span>Required hours:</span>
              </div>
              <div className={styles.column2}>
                <span>{gesamtSollStunden}</span>
              </div>
              <div className={styles.column1}>
                <span>Hours:</span>
              </div>
              <div className={styles.column2}>
                <span>{gesamtStunden}</span>
              </div>
              <div className={styles.column1}>
                <span>Total hours:</span>
              </div>
              <div className={styles.column2}>
                <span>{istStunden}</span>
              </div>
              {numberLabStunden > 0 && (
                <>
                  <div className={styles.column1}>
                    <span>Lab work:</span>
                  </div>
                  <div className={styles.column2}>
                    <span>{labStunden}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GenerateMonth;
